import React from "react";
import { Controller } from "react-hook-form";

import { SettingsCard } from "../../../../components/card/SettingsCard";
import { SettingsInput } from "../../../../components/inputs/SettingsInput";
import { FieldLabel } from "../../../../components/newUI/text";
import { Swiper } from "../../../../components/swiper/Swiper";
import { WorkTypeDetailsSectionProps } from "../model/workTypeEditor.types";
import {
  WORK_TYPE_DESCRIPTION_MAX,
  WORK_TYPE_NAME_MAX,
} from "../model/workTypeEditor.utils";
import { WorkTypeDefaultValuesSection } from "./WorkTypeDefaultValuesSection";

export const WorkTypeDetailsSection: React.FC<WorkTypeDetailsSectionProps> = ({
  control,
  isEditing,
  isActive,
  workType,
  onActiveChange,
  isActiveChanged,
}) => (
  <SettingsCard extraClasses={"p-6 mb-4"}>
    <div className={"mb-4 text-xl font-semibold text-[#2B333C]"}>
      Work Type Details
    </div>
    <div className={"flex items-center gap-6"}>
      <div className={"w-1/4"}>
        <Controller
          control={control}
          rules={{
            required: "This field is required",
            maxLength: {
              value: WORK_TYPE_NAME_MAX,
              message: `Max ${WORK_TYPE_NAME_MAX} characters`,
            },
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <>
              <FieldLabel label={"Title"} required />
              <SettingsInput
                value={value}
                isError={!!error}
                maxLength={WORK_TYPE_NAME_MAX}
                onChange={(e) => onChange(e.target.value)}
                placeholder={"Title..."}
                inputClasses={"w-full"}
              />
              <CharCount
                value={value}
                max={WORK_TYPE_NAME_MAX}
                error={error?.message}
              />
            </>
          )}
          name={"name"}
        />
      </div>
      <div className={"w-2/4"}>
        <Controller
          control={control}
          rules={{
            maxLength: {
              value: WORK_TYPE_DESCRIPTION_MAX,
              message: `Max ${WORK_TYPE_DESCRIPTION_MAX} characters`,
            },
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <>
              <FieldLabel label={"Description (optional)"} />
              <SettingsInput
                value={value}
                isError={!!error}
                maxLength={WORK_TYPE_DESCRIPTION_MAX}
                onChange={(e) => onChange(e.target.value)}
                placeholder={"This work type is used for..."}
                inputClasses={"w-full"}
              />
              <CharCount
                value={value}
                max={WORK_TYPE_DESCRIPTION_MAX}
                error={error?.message}
              />
            </>
          )}
          name={"description"}
        />
      </div>
      <div className={"w-1/4"}>
        {isEditing && (
          <>
            <FieldLabel label={"Work Type Status"} />
            <Swiper
              value={isActive}
              onChange={onActiveChange}
              description={isActive ? "Active" : "Inactive"}
              descriptionPosition={"right"}
            />
            {isActiveChanged && (
              <div className={"mt-1 text-xs font-medium text-[#B54708]"}>
                Click Save Changes to apply
              </div>
            )}
          </>
        )}
      </div>
    </div>
    <WorkTypeDefaultValuesSection
      isVisible={isEditing && Boolean(workType)}
      control={control}
      workType={workType}
    />
  </SettingsCard>
);

/** "12/20" under a box (red when the limit is reached), or the field's error. */
const CharCount: React.FC<{
  value: string | undefined;
  max: number;
  error?: string;
}> = ({ value, max, error }) => {
  const length = (value || "").length;
  return (
    <div
      className={
        "mt-1 text-xs " +
        (error || length >= max ? "text-[#DC2626]" : "text-[#8A90A2]")
      }>
      {error || `${length}/${max}`}
    </div>
  );
};
